import React, { useState, useEffect, useRef } from 'react';
import { Menu, Stack, Badge, Tooltip, TextField, InputAdornment, IconButton } from '@mui/material';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  type MRT_Row,
  type MRT_RowSelectionState,
  MRT_TablePagination,
} from 'material-react-table';
import { GridFilterModel, GridLogicOperator, GridColDef } from '@mui/x-data-grid';
import { styled } from '@mui/material';
import { Button, Box } from 'components/index';
import { useQuery, useQueryClient } from 'react-query';
import GlossaryDialog from '../components/glossary/GlossaryDialog';
import { Division } from '../components/glossary/glossary.type';
import {
  Edit as EditIcon,
  ViewColumnOutlined as ViewColumnIcon,
  FilterList as FilterListIcon,
  FileUploadOutlined as FileUploadOutlinedIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { LineItem } from '../components/glossary/glossary.type';
import { toast } from 'react-toastify';
import { getDivisions } from '../components/glossary/glossary.api';
import AddNewLineItemDialog from '../components/lineItem/AddNewLineItemDialog';
import { useDebounce } from '@uidotdev/usehooks';
import { getAppliedFiltersCount } from '../components/filter-panel/filter-panel.util';
import { getFromLocalStorage, storeInLocalStorage } from '../common/utils/util.cache';
import { getQueryStringForSieve, getStringOperators } from 'common/utils/datagrid-sieve.util';
import FilterPanel from '../components/filter-panel/FilterPanel';
import { usePrompt } from '../components/AppPrompt';
import dataKeys from 'common/react-query/data-keys';
import { useMRTStyling } from '../hooks/useMRTStyling';
import { createLineItem, fetchPaginatedLineItems } from '../components/lineItem/lineitem.api';
import { CustomManageColumnsMenu } from '../components/common/CustomManageColumnsMenu';
import { deepCopy } from 'common/utils/common.util';
import TableSkeletonLoader from '../components/general/TableSkeletonLoader';
import WhiteFab from '../components/common/WhiteFab';
import { IconOverTextButton } from '../components/common/IconOverTextButton';
import { ButtonDivider } from '../components/common/ButtonDivider';
import { TypeOfFile, costAnalysisFilesToAccept } from '../components/file-upload/fileUpload.type';
import { ROUTES } from 'common/constants/urls';
import UploadDialog from '../components/file-upload/UploadDialog';
import { PermissionGate, useHasActionRole } from '../components/common/PermissionGate';

const DEFAULT_PAGE_SIZE = 25;
const DEBOUNCE_MS = 500;
const cacheKey = `lineitem-table-preferences`;
const FILTERS_KEY = 'lineitem-table-filters';
const DEFAULT_FILTERS: GridFilterModel = { items: [], logicOperator: GridLogicOperator.And };
const getInitialFilters = () => {
  return getFromLocalStorage(FILTERS_KEY) ?? DEFAULT_FILTERS;
};
const saveFilters = (filters: GridFilterModel) => {
  storeInLocalStorage(FILTERS_KEY, filters);
};

const StyledEditIcon = styled(EditIcon)<{ color?: string }>(({ theme, color = 'gray' }) => ({
  color: color,
  '&:hover': {
    color: theme.palette.primary.main,
  },
}));

type LineItemTable = {
  id: string;
  description: string;
  unit: string;
  division?: any;
};

const LineItemsList: React.FC = () => {
  const { commonMRTStyling, getRowStyling, getActionsColumnStyling, getOnSortingChangeWithToast } = useMRTStyling<LineItemTable>();
  const queryClient = useQueryClient();
  const hasEditAccess = useHasActionRole('EDIT_LINE_ITEM_DIVISIONS');
  const [paginationModel, setPaginationModel] = useState({ pageIndex: 0, pageSize: 50 });
  const [sortingModel, setSortingModel] = React.useState([{ id: 'description', desc: false }]);
  const [filterAnchorEl, setFilterAnchorEl] = React.useState<null | HTMLElement>(null);
  const [columnsMenuAnchorEl, setColumnsMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [filterModel, setFilterModel] = React.useState<GridFilterModel>(getInitialFilters());
  const [queryParams, setQueryParams] = React.useState(
    getQueryStringForSieve({ page: 0, pageSize: 50 }, [{ field: 'description', sort: 'asc' }], {
      items: [],
      logicOperator: GridLogicOperator.And,
    }),
  );
  const debouncedQueryParams = useDebounce(queryParams, DEBOUNCE_MS); // delay the request by DEBOUNCE_MS
  const [searchText, setSearchText] = useState('');
  const debouncedSearchText = useDebounce(searchText, DEBOUNCE_MS);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  //   const debouncedPaginationModel = useDebounce(paginationModel, 500);

  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [selectedLineItem, setSelectedLineItem] = React.useState<LineItem | null>(null);
  const [glossaryDialogOpen, setGlossaryDialogOpen] = React.useState(false);
  const [totalItems, setTotalItems] = useState(0); // Total number of items
  const [isLineItemDialogOpen, setIsLineItemDialogOpen] = React.useState(false);
  const [selectedLineItems, setSelectedLineItems] = useState<LineItem[]>([]);
  const rowCountRef = React.useRef(0);
  const [isBulkEditMode, setIsBulkEditMode] = useState(false);
  const selectedLineItemsRef = useRef<LineItem[]>([]);
  selectedLineItemsRef.current = selectedLineItems;
  const [divisions, setDivisions] = useState<Division[]>([]);
  const { getConfirmation } = usePrompt();
  const [initialDataLoaded, setInitialDataLoaded] = useState(true);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [rowSelection, setRowSelection] = React.useState<MRT_RowSelectionState>({});

  const showUploadDialog = () => setIsUploadDialogOpen(true);
  const hideUploadDialog = () => setIsUploadDialogOpen(false);

  const handleOpenLineItemDialog = () => {
    setIsLineItemDialogOpen(true);
    // setAnchorEl(null);
  };

  const fetchLineItems = async () => {
    // Use debouncedQueryParams to fetch data
    const queryString = debouncedQueryParams.join('&');
    return await fetchPaginatedLineItems(debouncedQueryParams);
  };

  const { data, isLoading } = useQuery([dataKeys.lineitems, debouncedQueryParams], fetchLineItems, {
    onSettled: () => {
      // Set to false on the first settlement of the query
      setInitialDataLoaded(false);
    },
  });

  useEffect(() => {
    const fetchDivisions = async () => {
      try {
        const divisionsData = await getDivisions();
        setDivisions(divisionsData);
      } catch (error) {
        console.error('Error fetching divisions:', error);
        // Handle error appropriately
      }
    };

    fetchDivisions();
  }, []); // Empty dependency array to run only once on mount

  const handleEditSelected = () => {
    if (selectedLineItemsRef.current.length > 0) {
      setIsBulkEditMode(true);
      setGlossaryDialogOpen(true);
    }
  };

  const handleSaveLineItem = async (newLineItemData: any) => {
    try {
      // Replace with your actual endpoint URL
      await createLineItem(newLineItemData);
      toast.success('Line item added successfully');
      handleCloseLineItemDialog(); // Close the dialog
      queryClient.invalidateQueries([dataKeys.lineitems, debouncedQueryParams]);
    } catch (error: any) {
      let errormessage = 'Error creating line item.';
      errormessage = error.message;
      console.error(error);
      toast.error(errormessage);
    }
  };

  React.useEffect(() => {
    const selectedItems = (data?.items || []).filter((lineItem: any) => Object.keys(rowSelection).includes(lineItem.id));
    setSelectedLineItems(selectedItems);
  }, [rowSelection, data?.items]);

  const handleCloseLineItemDialog = () => {
    setIsLineItemDialogOpen(false);
  };

  const handleRowClick = (row: MRT_Row<LineItemTable>) => {
    const lineItem = (data?.items || []).find((item: any) => item.id === row.original.id);
    setSelectedLineItem(lineItem);
    setGlossaryDialogOpen(true);
    setIsBulkEditMode(false);
  };

  // const handleCellClick = (params: any) => {
  //   if (params.field === 'division' && !showCheckboxes) {
  //     const row = params.row as LineItem;
  //     setSelectedLineItem(row);
  //     setGlossaryDialogOpen(true);
  //     setIsBulkEditMode(false);
  //   }
  // };

  const handleGlossaryDialogClose = () => {
    setGlossaryDialogOpen(false);
    queryClient.invalidateQueries([dataKeys.lineitems, debouncedQueryParams]);
    // queryClient.invalidateQueries(keys.projectLineItemsAndBudgets);
  };

  // Define your columns for the MaterialReactTable
  const columns = React.useMemo<MRT_ColumnDef<LineItemTable>[]>(() => {
    const base: MRT_ColumnDef<LineItemTable>[] = [
      {
        accessorKey: 'description',
        header: 'Line Item Description',
        size: 300,
      },
      {
        accessorKey: 'unit',
        header: 'Unit',
        size: 150,
        Cell: ({ row }: { row: MRT_Row<LineItemTable> }) => {
          return row.original.unit ?? '--';
        },
      },
      {
        accessorKey: 'division',
        header: 'Division',
        size: 200,
        Cell: ({ row }: { row: MRT_Row<LineItemTable> }) => {
          const division = row.original.division;
          return division ? `${division.code} - ${division.name}` : '--';
        },
      },
    ];

    const actionColumn: MRT_ColumnDef<LineItemTable> = {
      id: 'actions',
      header: '',
      size: 60,
      minSize: 60,
      enableSorting: false,
      enableColumnFilter: false,
      enableColumnActions: false,
      enableHiding: false,
      ...getActionsColumnStyling(),
      Header: ({ table }: { table: any }) => (
        <Stack sx={{ width: '100%' }} direction='row' spacing={1} alignItems='center' justifyContent='center'>
          <Tooltip title='Edit Selected Line Items' arrow={false}>
            <span>
              <WhiteFab
                aria-label='edit'
                icon={<EditIcon fontSize='small' />}
                textColor='primary'
                onClick={handleEditSelected}
                size='small'
                disabled={selectedLineItemsRef.current.length === 0}
              />
            </span>
          </Tooltip>
        </Stack>
      ),
      Cell: ({ row }: { row: MRT_Row<LineItemTable> }) => (
        <Stack direction='row' spacing={1} alignItems='center' justifyContent='center' sx={{ width: '100%' }}>
          <Tooltip title='Edit Line Item' arrow={false}>
            <span>
              <WhiteFab
                aria-label='edit'
                icon={<EditIcon fontSize='small' />}
                textColor='primary'
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  e.preventDefault();
                  const lineItem = {
                    ...row.original,
                    divisionId: row.original.division?.id || null,
                  } as LineItem;
                  setSelectedLineItem(lineItem);
                  setGlossaryDialogOpen(true);
                  setIsBulkEditMode(false);
                }}
                size='small'
                disabled={false}
              />
            </span>
          </Tooltip>
        </Stack>
      ),
    };

    return hasEditAccess ? [...base, actionColumn] : base;
  }, [selectedLineItemsRef.current.length, hasEditAccess]);

  // Create compatible GridColDef for FilterPanel
  const filterColumns: GridColDef[] = [
    {
      field: 'description',
      headerName: 'Line Item Description',
      type: 'string',
      filterOperators: getStringOperators(),
    },
    {
      field: 'unit',
      headerName: 'Unit',
      type: 'string',
      filterOperators: getStringOperators(),
    },
    {
      field: 'division',
      headerName: 'Division',
      type: 'string',
      filterOperators: getStringOperators(),
    },
  ];

  const rowCount = React.useMemo(() => {
    if (data && data.totalCount !== undefined) {
      rowCountRef.current = data.totalCount;
    }
    return rowCountRef.current;
  }, [data?.totalCount]);

  useEffect(() => {
    if (data) {
      setTotalItems(data.totalCount);
    }
  }, [data]);

  React.useEffect(() => {
    const newFilterModel: GridFilterModel = deepCopy(filterModel);
    // Keep search synced as a description contains filter
    const otherItems = newFilterModel.items.filter(item => item.field !== 'description' || item.operator !== 'contains');
    if (debouncedSearchText && debouncedSearchText.trim().length > 0) {
      otherItems.push({ field: 'description', operator: 'contains', value: debouncedSearchText.trim() });
    }
    newFilterModel.items = otherItems;
    newFilterModel.items.forEach(item => {
      if (item.field == 'division' && item.operator != 'equals') {
        item.field = 'division.name';
      }
    });
    setQueryParams(
      getQueryStringForSieve(
        { page: paginationModel.pageIndex, pageSize: paginationModel.pageSize },
        sortingModel.map(sort => ({ field: sort.id, sort: sort.desc ? 'desc' : 'asc' })),
        newFilterModel,
      ),
    );
  }, [paginationModel, sortingModel, filterModel, debouncedSearchText]);

  const toggleUnassignedFilter = () => {
    const divisionFilter = filterModel.items.find(item => item.field === 'division' && item.value === 'null' && item.operator === 'equals');
    if (divisionFilter) {
      setFilterModel({ ...filterModel, items: filterModel.items.filter(item => item.field !== 'division') });
    } else {
      setFilterModel({
        ...filterModel,
        items: [
          ...filterModel.items,
          {
            field: 'division',
            operator: 'equals',
            value: 'null',
          },
        ],
      });
    }
  };

  const unassignedFilterOn = React.useMemo(() => {
    return filterModel.items.some(item => item.field === 'division' && item.value === 'null' && item.operator === 'equals');
  }, [filterModel]);

  const showManageColumns = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setColumnsMenuAnchorEl(e.currentTarget);
  };

  const table = useMaterialReactTable<LineItemTable>({
    ...commonMRTStyling,
    columns,
    data: data?.items || [],
    enableMultiRowSelection: hasEditAccess,
    enableRowSelection: hasEditAccess,
    columnResizeMode: 'onChange',
    muiPaginationProps: {
      rowsPerPageOptions: [10, 25, 50, 100],
      showFirstButton: true,
      showLastButton: true,
    },

    getRowId: (row: LineItemTable) => row.id,
    onPaginationChange: setPaginationModel,
    onSortingChange: getOnSortingChangeWithToast(sortingModel as any, setSortingModel as any),
    onRowSelectionChange: setRowSelection,
    state: {
      pagination: paginationModel,
      sorting: sortingModel,
      density: 'compact',
      isLoading: isLoading,
      rowSelection,
    },
    rowCount: rowCount,
    pageCount: Math.ceil(rowCount / paginationModel.pageSize),
    muiTableBodyRowProps: ({ row }: { row: MRT_Row<LineItemTable> }) => {
      // Determine row background color
      let backgroundColor = '';

      // Use zebra striping for alternating rows
      backgroundColor = row.index % 2 === 0 ? '#f9f9f9' : '#ffffff';

      return {
        sx: {
          backgroundColor: backgroundColor,
          '&:hover': {
            backgroundColor: (theme: any) => theme.palette.action.hover,
          },
          // Style pinned columns to match row background
          '& td[data-pinned="true"]::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: backgroundColor,
            zIndex: -1,
          },
          '& td[data-pinned="true"]': {
            zIndex: 5,
          },
        },
      };
    },
    muiTableBodyCellProps: ({ row }: { row: MRT_Row<LineItemTable> }) => ({
      sx: {
        border: 'none',
        backgroundColor: row.index % 2 === 0 ? '#f9f9f9' : '#ffffff',
      },
    }),

    muiBottomToolbarProps: {
      sx: {
        backgroundColor: 'white !important',
        borderRadius: 'inherit',
        border: '1px solid rgba(224, 224, 224, 1)',
      },
    },
    initialState: {
      density: 'compact',
      columnPinning: {
        left: ['mrt-row-select'],
        right: ['actions'],
      },
    },
    renderBottomToolbar: ({ table }) => (
      <Stack p={1} direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
        <PermissionGate actionRole='ADD_LINE_ITEM'>
          <Button onClick={handleOpenLineItemDialog}>Add New</Button>
        </PermissionGate>
        <Box sx={{ flex: 1 }} />
        <MRT_TablePagination table={table} />
      </Stack>
    ),
  });

  if (isLoading && initialDataLoaded) return <TableSkeletonLoader />;

  return (
    <Box mt={2}>
      <Box sx={{ height: '83vh' }}>
        <Menu
          anchorEl={filterAnchorEl}
          open={Boolean(filterAnchorEl)}
          onClose={() => {
            setFilterAnchorEl(null);
          }}
        >
          <FilterPanel columns={filterColumns} filterModel={filterModel} setFilterModel={setFilterModel} />
        </Menu>
        <Stack direction={'row'} alignItems='center' justifyContent={'flex-end'} p={1} spacing={2}>
          <Stack direction={'row'} alignItems={'center'} spacing={0}>
            <IconOverTextButton
              icon={<SearchIcon />}
              text='Search'
              tooltip='Search line items'
              onClick={() => setIsSearchOpen(!isSearchOpen)}
              sunken={isSearchOpen}
              showHoverEffect={true}
            />
            <Box
              sx={{
                ml: isSearchOpen ? 1 : 0,
                mr: 0,
                width: isSearchOpen ? '220px' : 0,
                transition: 'width 0.3s ease-in-out, opacity 0.3s ease-in-out, margin 0.3s ease-in-out',
                overflow: 'hidden',
                opacity: isSearchOpen ? 1 : 0,
              }}
            >
              <TextField
                size='medium'
                placeholder='Search'
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                InputProps={{
                  startAdornment: null,
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton size='small' aria-label='clear search' onClick={() => setSearchText('')}>
                        <ClearIcon fontSize='small' />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <IconOverTextButton
              icon={<FileUploadOutlinedIcon />}
              text='Upload Cost Analysis'
              tooltip='Upload Cost Analysis file'
              onClick={showUploadDialog}
              showHoverEffect={true}
            />

            <ButtonDivider />

            <IconOverTextButton
              icon={<ViewColumnIcon />}
              text='Columns'
              tooltip='Manage Columns'
              onClick={showManageColumns}
              showHoverEffect={false}
              wrapInSpan={true}
            />

            <Badge badgeContent={getAppliedFiltersCount(filterModel)} color='primary' overlap='circular'>
              <IconOverTextButton
                icon={<FilterListIcon />}
                text='Filter'
                tooltip='Filter'
                onClick={(e: any) => setFilterAnchorEl(e.currentTarget)}
                showHoverEffect={false}
              />
            </Badge>
          </Stack>
        </Stack>
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: (theme: any) => theme.palette.background.paper,
            borderRadius: '8px',
            overflow: 'hidden',
          }}
        >
          <MaterialReactTable table={table} />
        </Box>
      </Box>

      <GlossaryDialog
        open={glossaryDialogOpen}
        onClose={handleGlossaryDialogClose}
        lineItemId={isBulkEditMode ? null : selectedLineItem?.id}
        lineItems={isBulkEditMode ? selectedLineItems : null}
        bulkEdit={isBulkEditMode}
      />
      <AddNewLineItemDialog
        open={isLineItemDialogOpen}
        onClose={handleCloseLineItemDialog}
        saveLineItem={handleSaveLineItem}
        divisions={divisions} // Pass the divisions state
      />
      <UploadDialog
        open={isUploadDialogOpen}
        onClose={hideUploadDialog}
        useSequentialUpload={true}
        filesToAccept={costAnalysisFilesToAccept}
        typeOfFile={TypeOfFile.CostAnalysis}
        navigatePath={ROUTES.LINEITEMS}
        hideTabs={true}
        defaultTab={0}
      />
      {/* Custom Manage Columns Menu */}
      <CustomManageColumnsMenu anchorEl={columnsMenuAnchorEl} setAnchorEl={setColumnsMenuAnchorEl} table={table} title='Manage Columns' />
    </Box>
  );
};

export default LineItemsList;
